import { FormEvent, useState } from "react";
import { AlertCircle, ArrowRight, Mail, MailCheck } from "lucide-react";
import { api } from "../lib/api";

/**
 * Anmeldung ausschließlich per Link ("Magic Link").
 *
 * Es gibt bewusst keine Passwörter mehr: E-Mail eintragen, Link in der
 * Nachricht anklicken, fertig. Nichts zu merken, nichts zu verlieren, und
 * kein Passwort, das gestohlen werden kann.
 *
 * Falls die E-Mail einmal nicht ankommt, gibt es einen Notzugang über die
 * Kommandozeile: `npm run login-link -- adresse@firma.de` (siehe
 * scripts/login-link.ts). Der braucht keinen E-Mail-Versand.
 */
export function Login() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [gesendet, setGesendet] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setError(null);
    setLoading(true);
    try {
      await api("/auth/magic-link", {
        method: "POST",
        body: { email },
        auth: false,
      });
      setGesendet(true);
    } catch {
      // Auch im Fehlerfall keine Auskunft darüber, ob es die Adresse gibt.
      setGesendet(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(26,26,46,0.06)_0%,transparent_70%)]"
      />
      <div className="relative w-full max-w-md animate-fade-in">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white shadow-elevated">
            <Logo />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            AuftragsManager
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {gesendet
              ? "Prüfen Sie Ihr Postfach."
              : "Wir schicken Ihnen einen Link zum Anmelden."}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-elevated">
          {gesendet ? (
            <div className="text-center">
              <MailCheck className="mx-auto h-10 w-10 text-emerald-600" />
              <p className="mt-3 font-medium text-slate-900">Link ist unterwegs</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">
                Wenn es zu <span className="font-medium">{email}</span> ein Konto
                gibt, liegt gleich eine E-Mail in Ihrem Postfach. Der Link gilt
                eine Stunde.
              </p>
              <button
                type="button"
                onClick={() => {
                  setGesendet(false);
                  setError(null);
                }}
                className="mt-5 text-sm text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
              >
                Andere Adresse verwenden
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <Field label="E-Mail" icon={<Mail className="h-4 w-4 text-slate-400" />}>
                <input
                  type="email"
                  className="input pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="ihre@email.de"
                />
              </Field>

              {error && (
                <p className="mt-4 inline-flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </p>
              )}

              <button type="submit" className="btn-primary mt-6 w-full" disabled={loading}>
                {loading ? (
                  "…"
                ) : (
                  <>
                    Link schicken <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
                Kein Passwort nötig — Sie melden sich über den Link in der
                E-Mail an.
              </p>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Geschützt durch AuftragsManager · DSGVO-konform
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          {icon}
        </span>
        {children}
      </div>
    </label>
  );
}

function Logo() {
  return (
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
      <path
        d="M9 22V10h6.5c2.5 0 4 1.4 4 3.5 0 1.7-1 2.9-2.6 3.3l3.1 5.2H17l-2.8-5h-2.7v5H9zm2.5-7h3.7c1.3 0 2.1-.6 2.1-1.7 0-1-.8-1.7-2.1-1.7h-3.7V15z"
        fill="currentColor"
      />
    </svg>
  );
}
